import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../components/auth/AuthContext";
import {
  Container,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Snackbar,
} from "@mui/material";
import axios from "axios";
import * as XLSX from "xlsx";
import {
  Document,
  Paragraph,
  Table as DocxTable,
  TableRow as DocxTableRow,
  TableCell as DocxTableCell,
  WidthType,
  AlignmentType,
  BorderStyle,
  HeadingLevel,
} from "docx";
import { saveAs } from "file-saver";
import * as docx from "docx";
import {
  ArrowUpward,
  Download,
  TransferWithinAStation,
} from "@mui/icons-material";

const AllStudents = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { schoolId: contextSchoolId, schoolName: contextSchoolName } =
    useAuth();
  const schoolId = location.state?.schoolId || contextSchoolId;
  const schoolName = location.state?.schoolName || contextSchoolName;

  const [learners, setLearners] = useState([]);
  const [filteredLearners, setFilteredLearners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({});
  const [schoolStreams, setSchoolStreams] = useState({});
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedStream, setSelectedStream] = useState(null);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printClass, setPrintClass] = useState("");
  const [printStream, setPrintStream] = useState("");
  const [downloadFormat, setDownloadFormat] = useState("excel");

  // Promotion states
  const [promotionDialogOpen, setPromotionDialogOpen] = useState(false);
  const [promotionClass, setPromotionClass] = useState("");
  const [promotionStream, setPromotionStream] = useState("");
  const [academicYear, setAcademicYear] = useState(
    new Date().getFullYear().toString()
  );
  const [selectedLearnersForPromotion, setSelectedLearnersForPromotion] =
    useState([]);
  const [promotionStep, setPromotionStep] = useState(0);
  const [promoting, setPromoting] = useState(false);
  const [promotionResult, setPromotionResult] = useState(null);
  const [promotionRemarks, setPromotionRemarks] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [selectedLearnersForTransfer, setSelectedLearnersForTransfer] = useState([]);
  const [transferData, setTransferData] = useState({
    transferReason: "change_of_school",
    remarks: "",
    destinationSchool: "",
  });
  const [transferring, setTransferring] = useState(false);

  // Function to get short subject names
  const getShortSubjectName = (subject) => {
    const shortNames = {
      Language: "Lang",
      Mathematics: "Math",
      "Psychomotor and Creative Arts": "PCA",
      Environmental: "Env",
      "Religious Education": "RE",
      "English Language": "Eng",
      "English Composition": "Comp",
      "Kiswahili Lugha": "Kisw",
      "Kiswahili Insha": "Insha",
      "Science and Technology": "Sci/Tech",
      "Science & Technology": "Sci/Tech",
      "Agriculture and Nutrition": "Agri/Nut",
      Agriculture: "Agri",
      "Social Studies": "SS",
      "Creative Arts & Sports": "CAS",
      "Creative Arts": "Arts",
      Science: "Sci",
      "Pre-Technical Studies": "PTS",
      "Pre-Technical": "Pre-Tech",
      "Number Work": "N/W",
      "Social Studies and Religious Education": "SS/RE",
      "Science and Environment": "Sci/Env",
      "Environmental Activities": "ARM",
      Kiswahili: "Kiswa",
      "Language/Total": "Lang/Total",
      "Reading/Jumla": "Read/Jum",
      "Kiswahili/Jumla": "Kisw/Jum",
      "Kiswahili Language/Kenya Sign Language": "Kisw/KSL",
    };
    return shortNames[subject] || subject;
  };

  // Get subjects by class
  const getSubjectsByClass = (className) => {
    if (["PG"].includes(className)) {
      return [
        "Number Work",
        "Language/Total",
        "Reading/Jumla",
        "Creative",
        "Science and Environment",
        "Social Studies and Religious Education",
      ];
    }

    if (["PP1", "PP2"].includes(className)) {
      return [
        "Number Work",
        "Language/Total",
        "Kiswahili/Jumla",
        "Creative Arts",
        "Science and Environment",
        "Social Studies and Religious Education",
      ];
    } else if (["Grade 1", "Grade 2", "Grade 3"].includes(className)) {
      return [
        "English",
        "Kiswahili",
        "Mathematics",
        "Science",
        "Social Studies and Religious Education",
        "Environmental Activities",
      ];
    } else if (["Grade 4", "Grade 5", "Grade 6"].includes(className)) {
      return [
        "English Language",
        "English Composition",
        "Kiswahili Lugha",
        "Kiswahili Insha",
        "Mathematics",
        "Science and Technology",
        "Agriculture and Nutrition",
        "Social Studies",
        "Religious Education",
        "Creative Arts & Sports",
      ];
    } else if (["Grade 7", "Grade 8", "Grade 9"].includes(className)) {
      return [
        "English Language",
        "English Composition",
        "Kiswahili Lugha",
        "Kiswahili Insha",
        "Mathematics",
        "Science",
        "Science and Technology",
        "Agriculture and Nutrition",
        "Pre-Technical Studies",
        "Social Studies",
        "Religious Education",
        "Creative Arts & Sports",
      ];
    } else if (["Grade 10", "Grade 11", "Grade 12"].includes(className)) {
      return [
        "English",
        "Kiswahili Language/Kenya Sign Language",
        "Mathematics",
        "Religious Education",
        "Science and Technology",
        "Agriculture and Nutrition",
        "Social Studies",
      ];
    }
    return [];
  };

  // Get next class for promotion
  const getNextClass = (currentClass) => {
    const classProgression = [
      "PP1",
      "PP2",
      "Grade 1",
      "Grade 2",
      "Grade 3",
      "Grade 4",
      "Grade 5",
      "Grade 6",
      "Grade 7",
      "Grade 8",
      "Grade 9",
      "Grade 10",
      "Grade 11",
      "Grade 12",
    ];

    const currentIndex = classProgression.indexOf(currentClass);
    if (currentIndex === -1 || currentIndex === classProgression.length - 1) {
      return null; // No next class
    }

    return classProgression[currentIndex + 1];
  };

  // Check if should graduate to alumni
  const shouldGraduateToAlumni = (currentClass) => {
    return currentClass === "Grade 9" || currentClass === "Grade 12";
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [learnersResponse, streamsResponse] = await Promise.all([
          axios.get("https://ed-9ny1.onrender.com/api/schools/learners", {
            params: { schoolId },
          }),
          axios.get(
            `https://ed-9ny1.onrender.com/api/schools/${schoolId}/streams`
          ),
        ]);
        setLearners(learnersResponse.data);
        setFilteredLearners(learnersResponse.data);
        if (streamsResponse.data.classStreams) {
          setSchoolStreams(streamsResponse.data.classStreams);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    if (schoolId) fetchData();
  }, [schoolId]);

  // useEffect(() => {
  //   let filtered = learners;

  //   // FILTER OUT ALUMNI from the main display
  //   filtered = filtered.filter((learner) => learner.status !== "alumni");

  //   if (searchQuery.trim() !== "") {
  //     filtered = filtered.filter(
  //       (learner) =>
  //         learner.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
  //         learner.admissionNumber
  //           .toLowerCase()
  //           .includes(searchQuery.toLowerCase()) ||
  //         (learner.birthCertificateNumber &&
  //           learner.birthCertificateNumber
  //             .toLowerCase()
  //             .includes(searchQuery.toLowerCase())) ||
  //         learner.parentName
  //           .toLowerCase()
  //           .includes(searchQuery.toLowerCase()) ||
  //         learner.parentEmail
  //           .toLowerCase()
  //           .includes(searchQuery.toLowerCase()) ||
  //         learner.parentContact
  //           .toLowerCase()
  //           .includes(searchQuery.toLowerCase()) ||
  //         learner.class.toLowerCase().includes(searchQuery.toLowerCase()) ||
  //         (learner.stream &&
  //           learner.stream.toLowerCase().includes(searchQuery.toLowerCase()))
  //     );
  //   }

  //   if (selectedClass) {
  //     filtered = filtered.filter((learner) => learner.class === selectedClass);
  //     if (selectedStream) {
  //       filtered = filtered.filter(
  //         (learner) => learner.stream === selectedStream
  //       );
  //     }
  //   }

  //   setFilteredLearners(filtered);
  // }, [searchQuery, learners, selectedClass, selectedStream]);

  useEffect(() => {
    let filtered = learners;

    // FILTER OUT ALUMNI from the main display
    filtered = filtered.filter((learner) => learner.status !== "alumni");

    if (searchQuery.trim() !== "") {
      const query = searchQuery.toLowerCase().trim();

      filtered = filtered.filter((learner) => {
        // Helper function to safely check string inclusion
        const includesQuery = (value) =>
          value &&
          typeof value === "string" &&
          value.toLowerCase().includes(query);

        return (
          includesQuery(learner.fullName) ||
          includesQuery(learner.admissionNumber) ||
          includesQuery(learner.birthCertificateNumber) ||
          includesQuery(learner.parentName) ||
          includesQuery(learner.parentEmail) ||
          includesQuery(learner.parentContact) ||
          includesQuery(learner.class) ||
          includesQuery(learner.stream)
        );
      });
    }

    if (selectedClass) {
      filtered = filtered.filter((learner) => learner.class === selectedClass);
      if (selectedStream) {
        filtered = filtered.filter(
          (learner) => learner.stream === selectedStream
        );
      }
    }

    setFilteredLearners(filtered);
  }, [searchQuery, learners, selectedClass, selectedStream]);

  const groupLearnersByClassAndStream = (learnersArray) => {
    const grouped = {};

    // First, filter out alumni for display
    const activeLearners = learnersArray.filter(
      (learner) => learner.status !== "alumni"
    );
    const alumniLearners = learnersArray.filter(
      (learner) => learner.status === "alumni"
    );

    activeLearners.forEach((learner) => {
      const { class: className, stream } = learner;
      const hasStreams =
        schoolStreams[className] && schoolStreams[className].length > 0;
      if (!grouped[className]) {
        grouped[className] = {
          hasStreams,
          streams: {},
          totalActive: 0,
          totalAlumni: 0,
        };
      }

      // Count alumni for this class
      grouped[className].totalAlumni = alumniLearners.filter(
        (alumni) => alumni.class === className
      ).length;

      const streamName = hasStreams ? stream || "No Stream" : "No Stream";
      if (!grouped[className].streams[streamName]) {
        grouped[className].streams[streamName] = [];
      }
      grouped[className].streams[streamName].push(learner);
      grouped[className].totalActive++;
    });

    // Also add classes that have only alumni (empty classes)
    const allClasses = [...new Set(learnersArray.map((l) => l.class))];
    allClasses.forEach((className) => {
      if (!grouped[className]) {
        const hasStreams =
          schoolStreams[className] && schoolStreams[className].length > 0;
        grouped[className] = {
          hasStreams,
          streams: {},
          totalActive: 0,
          totalAlumni: alumniLearners.filter(
            (alumni) => alumni.class === className
          ).length,
        };
      }
    });

    return grouped;
  };

  const sortLearners = (learnersInGroup, groupKey) => {
    if (!sortConfig[groupKey]) return learnersInGroup;
    const { key, direction } = sortConfig[groupKey];
    return [...learnersInGroup].sort((a, b) => {
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const requestSort = (groupKey, key) => {
    let direction = "asc";
    if (sortConfig[groupKey] && sortConfig[groupKey].key === key) {
      direction = sortConfig[groupKey].direction === "asc" ? "desc" : "asc";
    }
    setSortConfig({ ...sortConfig, [groupKey]: { key, direction } });
  };

  const resetFilters = () => {
    setSelectedClass(null);
    setSelectedStream(null);
    setSearchQuery("");
  };

  // Promotion functions
  const handleOpenPromotionDialog = (className, stream = "") => {
    setPromotionClass(className);
    setPromotionStream(stream);
    setPromotionDialogOpen(true);
    setPromotionStep(0);
    setSelectedLearnersForPromotion([]);
    setPromotionResult(null);
    setPromotionRemarks("");
  };

  const handleSelectAllForPromotion = () => {
    const learnersInClass = filteredLearners.filter(
      (learner) =>
        learner.class === promotionClass &&
        (promotionStream === "" || learner.stream === promotionStream)
    );

    console.log("Select All - Learners in class:", learnersInClass.length);

    // Check if we're selecting all or deselecting all
    const allSelected = learnersInClass.every((learner) =>
      selectedLearnersForPromotion.includes(learner._id)
    );

    if (allSelected) {
      // Deselect all
      setSelectedLearnersForPromotion([]);
      console.log("Deselected all learners");
    } else {
      // Select all (regardless of status)
      const allLearnerIds = learnersInClass.map((l) => l._id);
      setSelectedLearnersForPromotion(allLearnerIds);
      console.log(`Selected all ${allLearnerIds.length} learners`);
    }
  };

  const handleSelectLearnerForPromotion = (learnerId) => {
    // Simply toggle the selection without checking status
    if (selectedLearnersForPromotion.includes(learnerId)) {
      setSelectedLearnersForPromotion(
        selectedLearnersForPromotion.filter((id) => id !== learnerId)
      );
      console.log(`Deselected learner ID: ${learnerId}`);
    } else {
      setSelectedLearnersForPromotion([
        ...selectedLearnersForPromotion,
        learnerId,
      ]);
      console.log(`Selected learner ID: ${learnerId}`);
    }
  };






 // ========== ADD THESE TRANSFER FUNCTIONS HERE ==========
  const handleOpenTransferDialog = (learnerIds) => {
    setSelectedLearnersForTransfer(learnerIds);
    setTransferDialogOpen(true);
  };

  // In AllStudents.js, update the handleTransferLearners function:
const handleTransferLearners = async () => {
  if (selectedLearnersForTransfer.length === 0) return;

  try {
    setTransferring(true);
    const response = await axios.post(
      "https://ed-9ny1.onrender.com/api/schools/transfer-multiple",
      {
        learnerIds: selectedLearnersForTransfer,
        schoolId: schoolId,
        transferReason: transferData.transferReason,
        remarks: transferData.remarks,
        destinationSchool: transferData.destinationSchool,
        transferredBy: "System Admin", // This is fine now as string
      }
    );

    if (response.data.success) {
      setSnackbar({
        open: true,
        message: `Successfully transferred ${response.data.results.successful.length} learners`,
        severity: "success",
      });
      
      // Refresh learners data
      const learnersResponse = await axios.get(
        "https://ed-9ny1.onrender.com/api/schools/learners",
        { params: { schoolId } }
      );
      setLearners(learnersResponse.data);
      setFilteredLearners(learnersResponse.data);
      
      // Reset
      setTransferDialogOpen(false);
      setSelectedLearnersForTransfer([]);
      setTransferData({
        transferReason: "change_of_school",
        remarks: "",
        destinationSchool: "",
      });
    }
  } catch (error) {
    console.error("Error transferring learners:", error);
    setSnackbar({
      open: true,
      message: error.response?.data?.message || "Failed to transfer learners",
      severity: "error",
    });
  } finally {
    setTransferring(false);
  }
};
  // ========== END OF TRANSFER FUNCTIONS ==========





  const handlePromoteClass = async () => {
    if (selectedLearnersForPromotion.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select at least one learner to promote",
        severity: "error",
      });
      return;
    }

    setPromoting(true);
    try {
      let response;
      const isGraduatingClass = shouldGraduateToAlumni(promotionClass);
      const nextClass = getNextClass(promotionClass);
      const learnersInClass = filteredLearners.filter(
        (learner) =>
          learner.class === promotionClass &&
          (promotionStream === "" || learner.stream === promotionStream)
      );

      console.log("=== PROMOTION REQUEST ===");
      console.log("Action:", isGraduatingClass ? "Graduation" : "Promotion");
      console.log(
        "From:",
        promotionClass,
        promotionStream ? `(${promotionStream})` : ""
      );
      console.log("To:", isGraduatingClass ? "Alumni" : nextClass);
      console.log("Selected learners:", selectedLearnersForPromotion.length);
      console.log("Sample IDs:", selectedLearnersForPromotion.slice(0, 3));

      if (isGraduatingClass) {
        // SIMPLIFIED: Just send learnerIds
        const payload = {
          className: promotionClass,
          stream: promotionStream || undefined,
          graduationYear: academicYear,
          remarks: promotionRemarks,
          learnerIds: selectedLearnersForPromotion,
        };

        console.log("Sending graduation request:", payload);

        response = await axios.post(
          "https://ed-9ny1.onrender.com/api/schools/graduate-class-to-alumni",
          payload
        );
      } else {
        // Regular promotion
        const payload = {
          className: promotionClass,
          stream: promotionStream || undefined,
          academicYear: academicYear,
          remarks: promotionRemarks,
          learnerIds: selectedLearnersForPromotion,
        };

        console.log("Sending promotion request:", payload);

        response = await axios.post(
          "https://ed-9ny1.onrender.com/api/schools/promote-learners-by-class",
          payload
        );
      }

      if (response.data.success) {
        console.log("✅ Success:", response.data.message);

        setPromotionResult(response.data);
        setPromotionStep(2);

        // Refresh learners data
        const learnersResponse = await axios.get(
          "https://ed-9ny1.onrender.com/api/schools/learners",
          {
            params: { schoolId },
          }
        );
        setLearners(learnersResponse.data);
        setFilteredLearners(learnersResponse.data);

        setSnackbar({
          open: true,
          message: response.data.message,
          severity: "success",
        });
      } else {
        console.error("❌ Failed:", response.data.message);
        setSnackbar({
          open: true,
          message: response.data.message,
          severity: "error",
        });
      }
    } catch (error) {
      console.error("❌ Error:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to process request",
        severity: "error",
      });
    } finally {
      setPromoting(false);
    }
  };

  const renderPromotionStep = () => {
    const learnersInClass = filteredLearners.filter(
      (learner) =>
        learner.class === promotionClass &&
        (promotionStream === "" || learner.stream === promotionStream)
    );

    const nextClass = getNextClass(promotionClass);
    const isGraduatingClass = shouldGraduateToAlumni(promotionClass);

    switch (promotionStep) {
      case 0: // Select learners
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Learners to {isGraduatingClass ? "Graduate" : "Promote"}
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>{promotionClass}</strong>
              {promotionStream && ` - ${promotionStream}`}
              {isGraduatingClass
                ? ` → Graduate to Alumni`
                : ` → Promote to ${nextClass}`}
            </Alert>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography>
                Selected: {selectedLearnersForPromotion.length} of{" "}
                {learnersInClass.length} learners
              </Typography>
              <Button
                onClick={handleSelectAllForPromotion}
                variant="outlined"
                size="small"
              >
                {selectedLearnersForPromotion.length === learnersInClass.length
                  ? "Deselect All"
                  : "Select All"}
              </Button>
            </Box>

            <TableContainer sx={{ maxHeight: 400, overflow: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selectedLearnersForPromotion.length > 0 &&
                          selectedLearnersForPromotion.length <
                            learnersInClass.length
                        }
                        checked={
                          learnersInClass.length > 0 &&
                          selectedLearnersForPromotion.length ===
                            learnersInClass.length
                        }
                        onChange={handleSelectAllForPromotion}
                      />
                    </TableCell>
                    <TableCell>Adm No</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Stream</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Parent Contact</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {learnersInClass.map((learner) => (
                    <TableRow key={learner._id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedLearnersForPromotion.includes(
                            learner._id
                          )}
                          onChange={() =>
                            handleSelectLearnerForPromotion(learner._id)
                          }
                        />
                      </TableCell>
                      <TableCell>{learner.admissionNumber}</TableCell>
                      <TableCell>{learner.fullName}</TableCell>
                      <TableCell>{learner.stream || "N/A"}</TableCell>
                      <TableCell>
                        <Chip
                          label={learner.status || "active"}
                          size="small"
                          color={
                            learner.status === "alumni"
                              ? "secondary"
                              : "primary"
                          }
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{learner.parentContact}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );

      case 1: // Confirm details
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Confirm {isGraduatingClass ? "Graduation" : "Promotion"} Details
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Current Class
                    </Typography>
                    <Typography variant="h6">
                      {promotionClass}
                      {promotionStream && ` (${promotionStream})`}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Action
                    </Typography>
                    <Typography variant="h6">
                      {isGraduatingClass
                        ? "Graduate to Alumni"
                        : `Promote to ${nextClass}`}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Academic Year"
                  value={academicYear}
                  onChange={(e) => setAcademicYear(e.target.value)}
                  margin="normal"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Remarks (Optional)"
                  value={promotionRemarks}
                  onChange={(e) => setPromotionRemarks(e.target.value)}
                  margin="normal"
                  multiline
                  rows={3}
                  placeholder="Add any remarks about this promotion..."
                />
              </Grid>
            </Grid>

            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Important:</strong> This action cannot be undone.
              {isGraduatingClass
                ? " Graduated learners will be moved to the Alumni section."
                : " Promoted learners will be moved to the next class."}
            </Alert>
          </Box>
        );

      case 2: // Results
        if (!promotionResult) return null;

        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {isGraduatingClass ? "Graduation" : "Promotion"} Results
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Card sx={{ bgcolor: "#4CAF50", color: "white" }}>
                  <CardContent sx={{ textAlign: "center" }}>
                    <Typography variant="h3">
                      {promotionResult.summary?.graduated ||
                        promotionResult.summary?.promoted ||
                        0}
                    </Typography>
                    <Typography>
                      {isGraduatingClass ? "Graduated" : "Promoted"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ bgcolor: "#FF9800", color: "white" }}>
                  <CardContent sx={{ textAlign: "center" }}>
                    <Typography variant="h3">
                      {promotionResult.summary?.skipped || 0}
                    </Typography>
                    <Typography>Skipped</Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ bgcolor: "#F44336", color: "white" }}>
                  <CardContent sx={{ textAlign: "center" }}>
                    <Typography variant="h3">
                      {promotionResult.summary?.errors || 0}
                    </Typography>
                    <Typography>Errors</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {promotionResult.data?.errors?.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Errors occurred:
                </Typography>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {promotionResult.data.errors
                    .slice(0, 3)
                    .map((error, index) => (
                      <li key={index}>
                        <small>
                          {error.name}: {error.error}
                        </small>
                      </li>
                    ))}
                  {promotionResult.data.errors.length > 3 && (
                    <li>
                      <small>
                        ... and {promotionResult.data.errors.length - 3} more
                      </small>
                    </li>
                  )}
                </ul>
              </Alert>
            )}

            <Alert severity="success">
              {isGraduatingClass
                ? `Successfully graduated ${
                    promotionResult.summary?.graduated || 0
                  } learners to alumni.`
                : `Successfully promoted ${
                    promotionResult.summary?.promoted || 0
                  } learners to ${nextClass}.`}
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  const generateClassListExcel = (className, streamName, learnersList) => {
    // Get subjects for this class
    const subjects = getSubjectsByClass(className);

    // Get shortened subject names
    const shortSubjects = subjects.map((subject) =>
      getShortSubjectName(subject)
    );

    // Prepare worksheet data
    const worksheetData = [
      // Header row 1: School info
      [schoolName || "School Name", "", "", "", "", "", "", "", "", "", "", ""],
      // Header row 2: Class info
      [
        `Class List: ${className} ${
          streamName !== "No Stream" ? `- ${streamName}` : ""
        }`,
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
      ],
      // Header row 3: Year and date
      [
        `Year: ${new Date().getFullYear()} | Date: ${new Date().toLocaleDateString()}`,
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
      ],
      // Empty row
      [],
      // Column headers
      ["Adm No", "Name", ...shortSubjects],
    ];

    // Add student data
    learnersList
      .sort((a, b) => a.admissionNumber.localeCompare(b.admissionNumber))
      .forEach((learner) => {
        worksheetData.push([
          learner.admissionNumber,
          learner.fullName,
          ...Array(subjects.length).fill(""), // Empty cells for subjects
        ]);
      });

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths
    const colWidths = [
      { wch: 10 }, // Adm No
      { wch: 25 }, // Name
      ...shortSubjects.map(() => ({ wch: 10 })), // Subjects (shorter width for short names)
    ];
    ws["!cols"] = colWidths;

    // Merge header cells
    if (!ws["!merges"]) ws["!merges"] = [];
    // Merge school name across all columns
    ws["!merges"].push({
      s: { r: 0, c: 0 },
      e: { r: 0, c: Math.max(2 + shortSubjects.length - 1, 10) },
    });
    // Merge class info across all columns
    ws["!merges"].push({
      s: { r: 1, c: 0 },
      e: { r: 1, c: Math.max(2 + shortSubjects.length - 1, 10) },
    });
    // Merge date info across all columns
    ws["!merges"].push({
      s: { r: 2, c: 0 },
      e: { r: 2, c: Math.max(2 + shortSubjects.length - 1, 10) },
    });

    return ws;
  };

  const generateClassListWord = async (className, streamName, learnersList) => {
    // Get subjects for this class
    const subjects = getSubjectsByClass(className);

    // Get shortened subject names
    const shortSubjects = subjects.map((subject) =>
      getShortSubjectName(subject)
    );

    // Create table rows
    const tableRows = [];

    // Header row
    const headerCells = [
      new DocxTableCell({
        children: [
          new Paragraph({ text: "Adm No", alignment: AlignmentType.CENTER }),
        ],
        shading: { fill: "D3D3D3" },
        borders: {
          top: { style: BorderStyle.SINGLE, size: 1 },
          bottom: { style: BorderStyle.SINGLE, size: 1 },
          left: { style: BorderStyle.SINGLE, size: 1 },
          right: { style: BorderStyle.SINGLE, size: 1 },
        },
      }),
      new DocxTableCell({
        children: [
          new Paragraph({ text: "Name", alignment: AlignmentType.CENTER }),
        ],
        shading: { fill: "D3D3D3" },
        borders: {
          top: { style: BorderStyle.SINGLE, size: 1 },
          bottom: { style: BorderStyle.SINGLE, size: 1 },
          left: { style: BorderStyle.SINGLE, size: 1 },
          right: { style: BorderStyle.SINGLE, size: 1 },
        },
      }),
      ...shortSubjects.map(
        (subject) =>
          new DocxTableCell({
            children: [
              new Paragraph({ text: subject, alignment: AlignmentType.CENTER }),
            ],
            shading: { fill: "D3D3D3" },
            borders: {
              top: { style: BorderStyle.SINGLE, size: 1 },
              bottom: { style: BorderStyle.SINGLE, size: 1 },
              left: { style: BorderStyle.SINGLE, size: 1 },
              right: { style: BorderStyle.SINGLE, size: 1 },
            },
          })
      ),
    ];
    tableRows.push(new DocxTableRow({ children: headerCells }));

    // Student rows
    learnersList
      .sort((a, b) => a.admissionNumber.localeCompare(b.admissionNumber))
      .forEach((learner) => {
        const studentCells = [
          new DocxTableCell({
            children: [
              new Paragraph({
                text: learner.admissionNumber,
                alignment: AlignmentType.CENTER,
              }),
            ],
            borders: {
              top: { style: BorderStyle.SINGLE, size: 1 },
              bottom: { style: BorderStyle.SINGLE, size: 1 },
              left: { style: BorderStyle.SINGLE, size: 1 },
              right: { style: BorderStyle.SINGLE, size: 1 },
            },
          }),
          new DocxTableCell({
            children: [new Paragraph({ text: learner.fullName })],
            borders: {
              top: { style: BorderStyle.SINGLE, size: 1 },
              bottom: { style: BorderStyle.SINGLE, size: 1 },
              left: { style: BorderStyle.SINGLE, size: 1 },
              right: { style: BorderStyle.SINGLE, size: 1 },
            },
          }),
          ...Array(subjects.length)
            .fill("")
            .map(
              () =>
                new DocxTableCell({
                  children: [new Paragraph({ text: "" })],
                  borders: {
                    top: { style: BorderStyle.SINGLE, size: 1 },
                    bottom: { style: BorderStyle.SINGLE, size: 1 },
                    left: { style: BorderStyle.SINGLE, size: 1 },
                    right: { style: BorderStyle.SINGLE, size: 1 },
                  },
                })
            ),
        ];
        tableRows.push(new DocxTableRow({ children: studentCells }));
      });

    // Create the document
    const doc = new Document({
      sections: [
        {
          children: [
            new Paragraph({
              text: schoolName || "School Name",
              heading: HeadingLevel.HEADING_1,
              alignment: AlignmentType.CENTER,
            }),
            new Paragraph({
              text: `Class List: ${className} ${
                streamName !== "No Stream" ? `- ${streamName}` : ""
              }`,
              heading: HeadingLevel.HEADING_2,
              alignment: AlignmentType.CENTER,
            }),
            new Paragraph({
              text: `Year: ${new Date().getFullYear()} | Date: ${new Date().toLocaleDateString()}`,
              alignment: AlignmentType.CENTER,
            }),
            new Paragraph({ text: "" }), // Empty paragraph for spacing
            new DocxTable({
              width: { size: 100, type: WidthType.PERCENTAGE },
              rows: tableRows,
            }),
          ],
        },
      ],
    });

    // Generate the Word document
    const blob = await docx.Packer.toBlob(doc);
    return blob;
  };

  const handleDownloadAllStreams = async () => {
    const learnersByClassAndStream = groupLearnersByClassAndStream(learners);

    if (downloadFormat === "excel") {
      const workbook = XLSX.utils.book_new();

      Object.entries(learnersByClassAndStream).forEach(
        ([className, classData]) => {
          Object.entries(classData.streams).forEach(
            ([streamName, learnersList]) => {
              const ws = generateClassListExcel(
                className,
                streamName,
                learnersList
              );
              const sheetName = `${className}${
                streamName !== "No Stream" ? `-${streamName}` : ""
              }`.substring(0, 31);
              XLSX.utils.book_append_sheet(workbook, ws, sheetName);
            }
          );
        }
      );

      // Generate Excel file and download
      XLSX.writeFile(
        workbook,
        `${schoolName}_ClassLists_${new Date()
          .toLocaleDateString()
          .replace(/\//g, "-")}.xlsx`
      );
    } else if (downloadFormat === "word") {
      try {
        // Import JSZip properly
        const JSZipModule = await import("jszip");
        const JSZip = JSZipModule.default; // Use default export

        const zip = new JSZip();

        for (const [className, classData] of Object.entries(
          learnersByClassAndStream
        )) {
          for (const [streamName, learnersList] of Object.entries(
            classData.streams
          )) {
            const blob = await generateClassListWord(
              className,
              streamName,
              learnersList
            );
            const fileName = `${schoolName}_${className}${
              streamName !== "No Stream" ? `_${streamName}` : ""
            }_ClassList.docx`;
            zip.file(fileName, blob);
          }
        }

        zip.generateAsync({ type: "blob" }).then((content) => {
          saveAs(
            content,
            `${schoolName}_ClassLists_${new Date()
              .toLocaleDateString()
              .replace(/\//g, "-")}.zip`
          );
        });
      } catch (error) {
        console.error("Error creating zip file:", error);
      }
    }
  };

  const handleDownloadSelectedClass = async () => {
    if (!printClass) return;

    const learnersByClassAndStream = groupLearnersByClassAndStream(learners);
    const classData = learnersByClassAndStream[printClass];

    if (!classData) return;

    if (downloadFormat === "excel") {
      const workbook = XLSX.utils.book_new();

      if (printStream) {
        // Download specific stream
        const learnersList = classData.streams[printStream] || [];
        const ws = generateClassListExcel(
          printClass,
          printStream,
          learnersList
        );
        const sheetName = `${printClass}${
          printStream !== "No Stream" ? `-${printStream}` : ""
        }`.substring(0, 31);
        XLSX.utils.book_append_sheet(workbook, ws, sheetName);
      } else {
        // Download all streams for the class
        Object.entries(classData.streams).forEach(
          ([streamName, learnersList]) => {
            const ws = generateClassListExcel(
              printClass,
              streamName,
              learnersList
            );
            const sheetName = `${printClass}${
              streamName !== "No Stream" ? `-${streamName}` : ""
            }`.substring(0, 31);
            XLSX.utils.book_append_sheet(workbook, ws, sheetName);
          }
        );
      }

      // Generate Excel file and download
      XLSX.writeFile(
        workbook,
        `${schoolName}_${printClass}_ClassLists_${new Date()
          .toLocaleDateString()
          .replace(/\//g, "-")}.xlsx`
      );
    } else if (downloadFormat === "word") {
      if (printStream) {
        // Download specific stream
        const learnersList = classData.streams[printStream] || [];
        const blob = await generateClassListWord(
          printClass,
          printStream,
          learnersList
        );
        saveAs(
          blob,
          `${schoolName}_${printClass}${
            printStream !== "No Stream" ? `_${printStream}` : ""
          }_ClassList_${new Date()
            .toLocaleDateString()
            .replace(/\//g, "-")}.docx`
        );
      } else {
        try {
          // Download all streams for the class as a zip
          const JSZipModule = await import("jszip");
          const JSZip = JSZipModule.default;

          const zip = new JSZip();

          for (const [streamName, learnersList] of Object.entries(
            classData.streams
          )) {
            const blob = await generateClassListWord(
              printClass,
              streamName,
              learnersList
            );
            const fileName = `${schoolName}_${printClass}${
              streamName !== "No Stream" ? `_${streamName}` : ""
            }_ClassList.docx`;
            zip.file(fileName, blob);
          }

          // Generate and download the zip file
          zip.generateAsync({ type: "blob" }).then((content) => {
            saveAs(
              content,
              `${schoolName}_${printClass}_ClassLists_${new Date()
                .toLocaleDateString()
                .replace(/\//g, "-")}.zip`
            );
          });
        } catch (error) {
          console.error("Error creating zip file:", error);
        }
      }
    }

    setPrintDialogOpen(false);
  };

  const learnersByClassAndStream =
    groupLearnersByClassAndStream(filteredLearners);

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
    hover: { scale: 1.02, transition: { duration: 0.2 } },
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Container
      maxWidth="lg"
      sx={{ py: 4, bgcolor: "#f8f9fa", minHeight: "100vh" }}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography
            variant="h4"
            sx={{ fontWeight: "bold", color: "#2c3e50" }}
          >
            All Students
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setPrintDialogOpen(true)}
            startIcon={<Download />}
          >
            Download Lists
          </Button>

          <Button
            variant="outlined"
            color="secondary"
            startIcon={<TransferWithinAStation />}
            onClick={() => navigate("/transferred-learners")}
          >
            View Transferred Learners
          </Button>
        </Box>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Search students..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="outlined"
            sx={{ bgcolor: "#fff" }}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <FormControl sx={{ minWidth: 120, maxWidth: 200 }}>
            <InputLabel>Class</InputLabel>
            <Select
              value={selectedClass || ""}
              onChange={(e) => {
                setSelectedClass(e.target.value || null);
                setSelectedStream(null);
              }}
              label="Class"
            >
              <MenuItem value="">All Classes</MenuItem>
              {Object.keys(learnersByClassAndStream).map((className) => (
                <MenuItem key={className} value={className}>
                  {className}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedClass && schoolStreams[selectedClass]?.length > 0 && (
            <FormControl sx={{ minWidth: 120, maxWidth: 200 }}>
              <InputLabel>Stream</InputLabel>
              <Select
                value={selectedStream || ""}
                onChange={(e) => setSelectedStream(e.target.value || null)}
                label="Stream"
              >
                <MenuItem value="">All Streams</MenuItem>
                {schoolStreams[selectedClass].map((stream) => (
                  <MenuItem key={stream} value={stream}>
                    {stream}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {(selectedClass || selectedStream) && (
            <Button variant="contained" color="error" onClick={resetFilters}>
              Reset
            </Button>
          )}
        </Box>

        {Object.entries(learnersByClassAndStream).map(
          ([className, classData]) => (
            <Box key={className} sx={{ mb: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ fontWeight: "bold", color: "#333" }}
                >
                  {className}
                  {shouldGraduateToAlumni(className) && (
                    <Chip
                      label="Graduating Class"
                      color="primary"
                      size="small"
                      sx={{ ml: 1, verticalAlign: "middle" }}
                    />
                  )}
                </Typography>

                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<ArrowUpward />}
                  onClick={() => handleOpenPromotionDialog(className)}
                >
                  {shouldGraduateToAlumni(className)
                    ? "Graduate Class"
                    : "Promote Class"}
                </Button>
              </Box>

              {Object.entries(classData.streams).map(
                ([streamName, learnersInStream]) => (
                  <Box key={`${className}-${streamName}`} sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                        pl: 1,
                      }}
                    >
                      {classData.hasStreams && (
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: "600", color: "#555" }}
                        >
                          Stream: {streamName}
                        </Typography>
                      )}

                      {classData.hasStreams && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<ArrowUpward />}
                          onClick={() =>
                            handleOpenPromotionDialog(className, streamName)
                          }
                        >
                          {shouldGraduateToAlumni(className)
                            ? "Graduate Stream"
                            : "Promote Stream"}
                        </Button>
                      )}
                    </Box>

                    <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() =>
                          requestSort(`${className}-${streamName}`, "fullName")
                        }
                      >
                        Sort by Name{" "}
                        {sortConfig[`${className}-${streamName}`]?.key ===
                        "fullName"
                          ? sortConfig[`${className}-${streamName}`]
                              ?.direction === "asc"
                            ? "↑"
                            : "↓"
                          : ""}
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() =>
                          requestSort(
                            `${className}-${streamName}`,
                            "admissionNumber"
                          )
                        }
                      >
                        Sort by Admission{" "}
                        {sortConfig[`${className}-${streamName}`]?.key ===
                        "admissionNumber"
                          ? sortConfig[`${className}-${streamName}`]
                              ?.direction === "asc"
                            ? "↑"
                            : "↓"
                          : ""}
                      </Button>
                    </Box>

                                       <Grid container spacing={2}>
                      {sortLearners(
                        learnersInStream,
                        `${className}-${streamName}`
                      ).map((learner) => (
                        <Grid item xs={12} sm={6} md={4} key={learner._id}>
                          <motion.div
                            variants={cardVariants}
                            initial="hidden"
                            animate="visible"
                            whileHover="hover"
                          >
                            <Card
                              sx={{
                                p: 2,
                                boxShadow: 3,
                                bgcolor: "#fff",
                                cursor: "pointer",
                              }}
                              onClick={() =>
                                navigate("/student-details", {
                                  state: { learner },
                                })
                              }
                            >
                              <CardContent>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: "bold",
                                    color: "#333",
                                    mb: 1,
                                  }}
                                >
                                  {learner.fullName}
                                </Typography>
                                <Typography
                                  sx={{ color: "#666", fontSize: "0.875rem" }}
                                >
                                  Admission: {learner.admissionNumber}
                                </Typography>
                                <Typography
                                  sx={{ color: "#666", fontSize: "0.875rem" }}
                                >
                                  Birth Cert:{" "}
                                  {learner.birthCertificateNumber || "N/A"}
                                </Typography>
                                <Typography
                                  sx={{ color: "#666", fontSize: "0.875rem" }}
                                >
                                  Parent: {learner.parentName}
                                </Typography>
                                <Typography
                                  sx={{ color: "#666", fontSize: "0.875rem" }}
                                >
                                  Email: {learner.parentEmail}
                                </Typography>
                                <Typography
                                  sx={{ color: "#666", fontSize: "0.875rem" }}
                                >
                                  Contact: {learner.parentContact}
                                </Typography>
                                <Typography
                                  sx={{ color: "#666", fontSize: "0.875rem" }}
                                >
                                  Class: {learner.class}
                                </Typography>
                                {learner.stream && (
                                  <Typography
                                    sx={{ color: "#666", fontSize: "0.875rem" }}
                                  >
                                    Stream: {learner.stream}
                                  </Typography>
                                )}
                                
                                {/* ========== ADD THIS TRANSFER BUTTON HERE ========== */}
                                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenTransferDialog([learner._id]);
                                    }}
                                  >
                                    Transfer
                                  </Button>
                                </Box>
                                {/* ========== END OF TRANSFER BUTTON ========== */}
                              </CardContent>
                            </Card>
                          </motion.div>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )
              )}
            </Box>
          )
        )}

        {/* Download Dialog */}
        <Dialog
          open={printDialogOpen}
          onClose={() => setPrintDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Download Class Lists</DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Download Format:
              </Typography>
              <ToggleButtonGroup
                value={downloadFormat}
                exclusive
                onChange={(e, newFormat) =>
                  newFormat && setDownloadFormat(newFormat)
                }
                aria-label="download format"
              >
                <ToggleButton value="excel" aria-label="excel">
                  Excel
                </ToggleButton>
                <ToggleButton value="word" aria-label="word">
                  Word
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Class</InputLabel>
              <Select
                value={printClass}
                onChange={(e) => {
                  setPrintClass(e.target.value);
                  setPrintStream("");
                }}
                label="Class"
              >
                <MenuItem value="">All Classes</MenuItem>
                {Object.keys(learnersByClassAndStream).map((className) => (
                  <MenuItem key={className} value={className}>
                    {className}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {printClass && schoolStreams[printClass]?.length > 0 && (
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Stream</InputLabel>
                <Select
                  value={printStream}
                  onChange={(e) => setPrintStream(e.target.value)}
                  label="Stream"
                >
                  <MenuItem value="">All Streams</MenuItem>
                  {schoolStreams[printClass].map((stream) => (
                    <MenuItem key={stream} value={stream}>
                      {stream}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPrintDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleDownloadSelectedClass}
              variant="contained"
              disabled={!printClass}
            >
              Download Selected
            </Button>
            <Button
              onClick={handleDownloadAllStreams}
              variant="contained"
              color="secondary"
            >
              Download All Classes
            </Button>
          </DialogActions>
        </Dialog>




                {/* ========== ADD TRANSFER DIALOG HERE ========== */}
        {/* Transfer Dialog */}
        <Dialog
          open={transferDialogOpen}
          onClose={() => !transferring && setTransferDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Transfer Learners</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              You are transferring {selectedLearnersForTransfer.length} learner(s). 
              This will move them to the transferred learners section.
            </Alert>

            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Transfer Reason</InputLabel>
              <Select
                value={transferData.transferReason}
                onChange={(e) => setTransferData({ ...transferData, transferReason: e.target.value })}
                label="Transfer Reason"
              >
                <MenuItem value="change_of_school">Change of School</MenuItem>
                <MenuItem value="family_relocation">Family Relocation</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Destination School (Optional)"
              value={transferData.destinationSchool}
              onChange={(e) => setTransferData({ ...transferData, destinationSchool: e.target.value })}
              margin="normal"
            />

            <TextField
              fullWidth
              label="Remarks"
              value={transferData.remarks}
              onChange={(e) => setTransferData({ ...transferData, remarks: e.target.value })}
              margin="normal"
              multiline
              rows={3}
              placeholder="Add any remarks about this transfer..."
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setTransferDialogOpen(false)}
              disabled={transferring}
            >
              Cancel
            </Button>
            <Button
              onClick={handleTransferLearners}
              variant="contained"
              color="secondary"
              disabled={transferring}
            >
              {transferring ? (
                <CircularProgress size={24} />
              ) : (
                `Transfer ${selectedLearnersForTransfer.length} Learner(s)`
              )}
            </Button>
          </DialogActions>
        </Dialog>
        {/* ========== END OF TRANSFER DIALOG ========== */}






        {/* Promotion Dialog */}
        <Dialog
          open={promotionDialogOpen}
          onClose={() => {
            if (!promoting) {
              setPromotionDialogOpen(false);
              setPromotionStep(0);
              setPromotionResult(null);
            }
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {shouldGraduateToAlumni(promotionClass)
              ? "Graduate Class"
              : "Promote Class"}
          </DialogTitle>

          <DialogContent>
            <Stepper activeStep={promotionStep} sx={{ mb: 3 }}>
              <Step>
                <StepLabel>Select Learners</StepLabel>
              </Step>
              <Step>
                <StepLabel>Confirm Details</StepLabel>
              </Step>
              <Step>
                <StepLabel>Results</StepLabel>
              </Step>
            </Stepper>

            {renderPromotionStep()}
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => {
                if (promotionStep > 0) {
                  setPromotionStep(promotionStep - 1);
                } else {
                  setPromotionDialogOpen(false);
                  setPromotionStep(0);
                }
              }}
              disabled={promoting}
            >
              {promotionStep === 0 ? "Cancel" : "Back"}
            </Button>

            {promotionStep < 2 && (
              <Button
                onClick={() => {
                  if (promotionStep === 0) {
                    if (selectedLearnersForPromotion.length === 0) {
                      setSnackbar({
                        open: true,
                        message: "Please select at least one learner",
                        severity: "error",
                      });
                      return;
                    }
                    setPromotionStep(1);
                  } else if (promotionStep === 1) {
                    handlePromoteClass();
                  }
                }}
                variant="contained"
                color="primary"
                disabled={promoting}
              >
                {promoting ? (
                  <CircularProgress size={24} />
                ) : promotionStep === 1 ? (
                  shouldGraduateToAlumni(promotionClass) ? (
                    "Confirm Graduation"
                  ) : (
                    "Confirm Promotion"
                  )
                ) : (
                  "Next"
                )}
              </Button>
            )}

            {promotionStep === 2 && (
              <Button
                onClick={() => {
                  setPromotionDialogOpen(false);
                  setPromotionStep(0);
                  setPromotionResult(null);
                  setSelectedLearnersForPromotion([]);
                  setPromotionRemarks("");
                }}
                variant="contained"
                color="primary"
              >
                Done
              </Button>
            )}
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
          sx={{
            bgcolor: snackbar.severity === "error" ? "#e74c3c" : "#4CAF50",
          }}
        />
      </motion.div>
    </Container>
  );
};

export default AllStudents;
